import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { useAuth } from "../Auth/AuthContext";
import PasswordChangeModal from "../User/PasswordChangeModal";
import { FaUserCircle, FaEuroSign, FaFileInvoiceDollar, FaPercent, FaKey } from "react-icons/fa";
import { isOrgAdmin } from "../../utils/roles";

const euro = (v) =>
  (Number(v) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " €";

const fmtDate = (val) => {
  if (!val) return "—";
  const iso = val.includes("T") ? val : val.replace(" ", "T") + "Z";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  // Filtre par mois (YYYY-MM) sur la date de validation. Vide = tout l'historique.
  const [month, setMonth] = useState("");
  // Compteur animé du total
  const [displayTotal, setDisplayTotal] = useState(0);

  // Devis + total/compteur recalculés sur le mois filtré.
  const { quotes, total, count } = useMemo(() => {
    const all = data?.quotes || [];
    const filtered = all.filter(
      (q) => !month || String(q.validated_at || "").slice(0, 7) === month
    );
    const sum = Number(filtered.reduce((s, q) => s + (q.commission || 0), 0).toFixed(2));
    return {
      quotes: filtered,
      total: month ? sum : (Number(data?.total) || sum),
      count: month ? filtered.length : (data?.count ?? filtered.length),
    };
  }, [data, month]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get("/users/me/commissions");
        if (mounted) setData(res.data);
      } catch {
        if (mounted) setData({ currentRate: 0, total: 0, count: 0, quotes: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Anime le total de 0 → valeur finale
  useEffect(() => {
    if (!data) return;
    const target = Number(total) || 0;
    if (target <= 0) { setDisplayTotal(0); return; }
    const start = performance.now();
    const dur = 900;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayTotal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [total]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* En-tête profil */}
      <div className="flex items-center gap-4">
        <FaUserCircle className="text-5xl text-primary/70" />
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold tracking-tight text-base-content">
            {user?.username || t("profile.fallbackTitle")}
          </h1>
          <p className="text-sm text-base-content/50 mt-0.5 capitalize">
            {isOrgAdmin(user) ? t("profile.roleAdmin") : t("profile.roleCommercial")}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-base-content/50">
            {t("profile.monthFilter", "Mois")}
          </label>
          <div className="flex items-center gap-1">
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="input input-bordered input-sm bg-base-200 border-base-300 focus:border-primary focus:outline-none"
            />
            {month && (
              <button onClick={() => setMonth("")} className="btn btn-ghost btn-sm" title={t("profile.allMonths", "Tout")}>
                ✕
              </button>
            )}
          </div>
        </div>
        <button
          onClick={() => setShowPasswordModal(true)}
          className="btn btn-outline btn-sm gap-2"
        >
          <FaKey size={13} /> {t("navbar.changePassword")}
        </button>
      </div>

      {/* Cartes récap */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 rounded-2xl p-5 card-lift">
          <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold">
            <FaEuroSign /> {t("profile.commissionsCumulated")}
          </div>
          <div className="mt-2 text-3xl font-extrabold text-emerald-700">
            {euro(displayTotal)}
          </div>
        </div>

        <div className="bg-base-100 border border-base-200 rounded-2xl p-5 card-lift">
          <div className="flex items-center gap-2 text-base-content/60 text-sm font-semibold">
            <FaFileInvoiceDollar /> {t("profile.validatedQuotes")}
          </div>
          <div className="mt-2 text-3xl font-extrabold text-base-content">
            {count || 0}
          </div>
        </div>

        <div className="bg-base-100 border border-base-200 rounded-2xl p-5 card-lift">
          <div className="flex items-center gap-2 text-base-content/60 text-sm font-semibold">
            <FaPercent /> {t("profile.currentRate")}
          </div>
          <div className="mt-2 text-3xl font-extrabold text-base-content">
            {data?.currentRate || 0} %
          </div>
        </div>
      </div>

      {/* Détail par devis */}
      <div className="bg-base-100 rounded-xl border border-base-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-base-200 text-sm font-semibold text-base-content/70">
          {t("profile.detailTitle")}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-base-200 border-b border-base-200 text-[11px] uppercase tracking-wider text-base-content/50">
                <th className="py-2.5 px-4 text-left">{t("profile.colQuote")}</th>
                <th className="py-2.5 px-4 text-left">{t("profile.colClient")}</th>
                <th className="py-2.5 px-4 text-left">{t("profile.colValidatedOn")}</th>
                <th className="py-2.5 px-4 text-right">{t("profile.colHT")}</th>
                <th className="py-2.5 px-4 text-right">{t("profile.colRate")}</th>
                <th className="py-2.5 px-4 text-right">{t("profile.colCommission")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-base-200">
              {(!quotes || quotes.length === 0) ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-sm text-base-content/40">
                    {t("profile.empty")}
                  </td>
                </tr>
              ) : (
                quotes.map((q) => (
                  <tr key={q.id} className="hover:bg-base-200/50 transition-colors">
                    <td className="py-2.5 px-4 text-sm text-base-content/60">#{q.id}</td>
                    <td className="py-2.5 px-4 text-sm font-medium text-base-content">{q.client_name}</td>
                    <td className="py-2.5 px-4 text-sm text-base-content/60">{fmtDate(q.validated_at)}</td>
                    <td className="py-2.5 px-4 text-sm text-right text-base-content/70">{euro(q.commissionBase ?? q.totalHT)}</td>
                    <td className="py-2.5 px-4 text-sm text-right text-base-content/50">{q.rate} %</td>
                    <td className="py-2.5 px-4 text-sm text-right font-semibold text-emerald-600">{euro(q.commission)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showPasswordModal && (
        <PasswordChangeModal onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
}
